import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { Star, X } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Decides whether the export-feedback modal should show for a project.
 * Suppresses permanently once the user has rated or dismissed feedback for the
 * project (server-persisted), and immediately within the session via the
 * dismissed-id set. Shared by both editor toolbars to avoid divergent logic.
 */
export function useExportFeedbackGate(
  workflowId: Id<"workflows"> | null | undefined,
  exports: Array<{ _id: string; status: string }> | undefined,
) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set())
  const hasResponded = useQuery(
    api.exportFeedback.hasRespondedForWorkflow,
    workflowId ? { workflowId } : 'skip',
  )

  const activeFeedbackExport = hasResponded
    ? undefined
    : exports?.find(
        (e) =>
          (e.status === 'processing' || e.status === 'pending') &&
          !dismissedIds.has(e._id),
      )

  const dismiss = (exportId: string) =>
    setDismissedIds((prev) => {
      const next = new Set(prev)
      next.add(exportId)
      return next
    })

  return { activeFeedbackExport, dismiss }
}

const STAR_LABELS = [
  '',
  'Not great',
  'Needs work',
  'Decent',
  'Really good',
  'Nailed it',
]

export function ExportFeedbackModal({
  workflowId,
  exportId,
  onDismiss,
}: {
  workflowId: Id<"workflows">
  exportId: string
  onDismiss: () => void
}) {
  const existingFeedback = useQuery(api.exportFeedback.getForExport, { exportId })
  const submitFeedback = useMutation(api.exportFeedback.submit)
  const skipFeedback = useMutation(api.exportFeedback.skip)

  const [rating, setRating] = useState(0)
  const [hoveredStar, setHoveredStar] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [visible, setVisible] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(id)
  }, [])

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        handleSkip()
      }
    }
    document.addEventListener('keydown', handleKey, true)
    return () => document.removeEventListener('keydown', handleKey, true)
  }, [])

  if (existingFeedback) return null

  const displayRating = hoveredStar || rating
  const canSubmit = rating > 0 && feedback.trim().length > 0 && !submitting

  function handleClose() {
    setVisible(false)
    setTimeout(onDismiss, 100)
  }

  async function handleSubmit() {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      await submitFeedback({
        workflowId,
        exportId,
        rating,
        feedback: feedback.trim(),
      })
      handleClose()
    } catch {
      setSubmitting(false)
    }
  }

  async function handleSkip() {
    try {
      await skipFeedback({ workflowId, exportId })
    } catch {
      // skip silently
    }
    handleClose()
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm" />
      <Card
        className={cn(
          'relative z-10 w-full max-w-md border-border bg-card shadow-xs mx-4 transition-all duration-100',
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97]',
        )}
      >
        <button
          type="button"
          onClick={handleSkip}
          className="absolute top-3 right-3 z-20 p-1 rounded-sm text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-8 pt-8 pb-6 flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-1.5 text-center">
            <p className="text-[15px] font-medium tracking-tight text-foreground">
              How did this turn out?
            </p>
            <p className="text-xs text-muted-foreground">
              Your video is exporting. Tell us how it went while you wait.
            </p>
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  onClick={() => {
                    setRating(star)
                    if (!feedback && textareaRef.current) {
                      setTimeout(() => textareaRef.current?.focus(), 100)
                    }
                  }}
                  className="group p-1 transition-transform duration-150 hover:scale-110 active:scale-95"
                >
                  <Star
                    size={28}
                    className={cn(
                      'transition-all duration-200',
                      star <= displayRating
                        ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_6px_rgba(251,191,36,0.35)]'
                        : 'fill-transparent text-muted-foreground/40 group-hover:text-muted-foreground/70',
                    )}
                  />
                </button>
              ))}
            </div>
            <span
              className={cn(
                'text-xs tracking-wide transition-all duration-200 h-4',
                displayRating > 0 ? 'text-muted-foreground' : 'text-transparent',
              )}
            >
              {STAR_LABELS[displayRating] || ''}
            </span>
          </div>

          <div
            className={cn(
              'w-full transition-opacity duration-300',
              rating > 0 ? 'opacity-100' : 'opacity-60',
            )}
          >
            <textarea
              ref={textareaRef}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={
                rating <= 2 && rating > 0
                  ? 'What fell short? Be specific — it helps.'
                  : rating >= 4
                    ? "What worked? Anything you'd still tweak?"
                    : 'What would have made this better?'
              }
              rows={3}
              className="w-full bg-background border border-input rounded-md px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 transition-colors duration-200"
            />
          </div>

          <div className="flex flex-col items-center gap-3 w-full">
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="w-full"
            >
              {submitting ? 'Sending...' : 'Submit'}
            </Button>
            <button
              type="button"
              onClick={handleSkip}
              className="text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors duration-200 tracking-wide"
            >
              skip
            </button>
          </div>
        </div>
      </Card>
    </div>
  )
}
